import type { AccountInfo } from '@aptos-labs/wallet-adapter-react';
import { aptosClient, resolveVaultConfig, VaultConfig } from './constants';
import { depositToVault } from './vaultTransactions';
import {
	getNextPosId,
	getPosition,
	recordDeposit,
	RecordDepositResponse,
} from 'api/client';
import { MAX_AMOUNT_ALLOWED } from 'data/constant';
import { notifySlackError } from 'utils/slack/slackNotifier';

export async function safeReadNextPosId(
	baseUrl?: string,
	vaultConfig?: Partial<VaultConfig>,
): Promise<number> {
	const config = resolveVaultConfig(vaultConfig);
	try {
		const { nextPosId } = await getNextPosId(baseUrl);
		if (!Number.isFinite(nextPosId) || nextPosId <= 0) {
			throw new Error('Invalid nextPosId from API');
		}
		return nextPosId;
	} catch {
		notifySlackError({
			title:
				'getNextPosId_api API Failed' +
				`API failed, falling back to fullnode for next_pos_id`,
			details: '',
		});
		try {
			const result = await aptosClient.view({
				payload: {
					function: `${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::next_pos_id`,
					functionArguments: [],
				},
			});
			const nextPosId = Number(result[0]);
			return nextPosId;
		} catch {
			notifySlackError({
				title: 'getNextPosId_fullnode Failed',
				details: '',
			});
			throw new Error(
				'Failed to get next position ID from both API and fullnode',
			);
		}
	}
}

export async function waitForNewPositionIndexed(
	posId: number,
	ownerAddress: string,
	timeoutMs = 20000,
	pollMs = 800,
	baseUrl?: string,
	vaultConfig?: Partial<VaultConfig>,
): Promise<[string, number, number, boolean] | null> {
	const t0 = Date.now();
	const ownerLower = ownerAddress.toLowerCase();

	while (Date.now() - t0 < timeoutMs) {
		try {
			const posData = await getPosition(posId, baseUrl);
			if (posData.result) {
				const [owner, vaultBalance, credited, finalized] =
					posData.result;
				const ownerStr = String(owner || '').toLowerCase();
				if (ownerStr === ownerLower) {
					return [
						String(owner),
						Number(vaultBalance),
						Number(credited),
						Boolean(finalized),
					];
				}
			}
		} catch {
			//
		}

		await new Promise((r) => setTimeout(r, pollMs));
	}
	return null;
}

export async function executeDepositFlow(
	account: AccountInfo,
	userId: string,
	amountUSDC: number,
	signAndSubmitFn: (payload: any) => Promise<{ hash: string }>,
	baseUrl?: string,
	vaultConfig?: Partial<VaultConfig>,
): Promise<{
	txHash: string;
	amountBase: string;
	posIdPromise: Promise<number>;
	recordDepositPromise: Promise<RecordDepositResponse>;
	positionPromise: Promise<[string, number, number, boolean] | null>;
}> {
	if (amountUSDC > MAX_AMOUNT_ALLOWED) {
		throw new Error(
			`Maximum deposit is ${MAX_AMOUNT_ALLOWED} USDC during beta`,
		);
	}

	const amountBase = Math.floor(amountUSDC * 1_000_000);

	// Submit transaction immediately after user interaction
	const txHash = await depositToVault(
		account,
		amountUSDC,
		signAndSubmitFn,
		vaultConfig,
	);

	const accountAddress =
		typeof account.address === 'string'
			? account.address
			: account.address.toString();

	// Kick off async work without blocking the UI
	const posIdPromise = safeReadNextPosId(baseUrl, vaultConfig);
	const recordDepositPromise: Promise<RecordDepositResponse> = recordDeposit(
		{
			txHash,
		},
		baseUrl,
	).catch((e: any) => {
		const errorBody = String(e?.message || '');
		if (errorBody.includes('404') || errorBody.includes('user not found')) {
			throw new Error(
				`Record deposit failed: User not found in database.\n` +
					`Make sure you signed in with Aptos Connect first.\n` +
					`User ID: ${userId}`,
			);
		}
		throw new Error(
			`Failed to record deposit in backend: ${errorBody}. ` +
				`Your transaction succeeded on-chain but may not appear immediately.`,
		);
	});

	const positionPromise = posIdPromise
		.then((nextPosId) =>
			waitForNewPositionIndexed(
				nextPosId,
				accountAddress,
				undefined,
				undefined,
				baseUrl,
				vaultConfig,
			),
		)
		.catch(() => null);

	return {
		amountBase: amountBase.toString(),
		txHash,
		posIdPromise,
		recordDepositPromise,
		positionPromise,
	};
}
